import React, { useState, useEffect, useRef } from 'react';

const WARNING_COUNTDOWN = 15; // seconds before auto-reset after warning appears

// ============================================================
// useIdleTimeout — tracks inactivity and triggers reset
//
// Options:
//   timeoutSeconds — idle time before warning (or auto-reset)
//   onReset        — called when session should reset (go home)
//   autoReset      — if true, skip warning and reset immediately
// ============================================================
export default function useIdleTimeout({ timeoutSeconds, onReset, autoReset = false }) {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(WARNING_COUNTDOWN);
  const [resetKey, setResetKey] = useState(0);

  const warningRef = useRef(false);
  const resetRef = useRef(onReset);
  resetRef.current = onReset;

  useEffect(() => {
    let idleTimer = null;
    let countdownTimer = null;

    function clear() {
      clearTimeout(idleTimer);
      clearInterval(countdownTimer);
    }

    function start() {
      clear();
      warningRef.current = false;
      setShowWarning(false);
      setCountdown(WARNING_COUNTDOWN);

      if (!timeoutSeconds || timeoutSeconds <= 0) return;

      idleTimer = setTimeout(() => {
        if (autoReset) {
          resetRef.current();
          return;
        }
        // Show "Still there?" warning with countdown
        warningRef.current = true;
        setShowWarning(true);
        let secs = WARNING_COUNTDOWN;
        setCountdown(secs);
        countdownTimer = setInterval(() => {
          secs--;
          setCountdown(secs);
          if (secs <= 0) {
            clearInterval(countdownTimer);
            resetRef.current();
          }
        }, 1000);
      }, timeoutSeconds * 1000);
    }

    start();

    // Restart idle timer on any user interaction (unless warning is showing)
    function onActivity() {
      if (!warningRef.current) start();
    }

    const events = ['touchstart', 'mousedown', 'keydown', 'scroll'];
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }));

    return () => {
      clear();
      events.forEach(e => window.removeEventListener(e, onActivity));
    };
  }, [timeoutSeconds, autoReset, resetKey]);

  // Dismiss warning and restart the idle timer
  function dismissWarning() {
    setResetKey(k => k + 1);
  }

  return { showWarning, countdown, dismissWarning };
}

// ============================================================
// IdleWarningModal — "Still there?" overlay with countdown
// ============================================================
export function IdleWarningModal({ countdown, onDismiss, onReset }) {
  return (
    <div className="kiosk-idle-overlay" onClick={onDismiss}>
      <div className="kiosk-idle-modal" onClick={e => e.stopPropagation()}>
        <div className="kiosk-idle-countdown">{countdown}</div>
        <div className="kiosk-idle-title">Still there?</div>
        <div className="kiosk-idle-sub">
          This screen will reset in {countdown} second{countdown !== 1 ? 's' : ''}
        </div>
        <button className="kiosk-idle-btn" onClick={onDismiss}>
          Yes, I'm here
        </button>
        <button className="kiosk-idle-start-over" onClick={onReset}>
          Start Over
        </button>
      </div>
    </div>
  );
}
